import React, { useState, useEffect } from "react";
import { Button } from "../components/common/Button";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { MealType } from "../types/index"; // central MealType
import {
  SubscriptionFrequency,
  SubscriptionDuration,
  SubscriptionConfig,
} from "../types/subscription";

// Pricing Constants
const MEAL_PRICES: Record<MealType, number> = {
  breakfast: 100,
  lunch: 150,
  dinner: 150,
  morning: 100,
  afternoon: 150,
  night: 150,
  evening_snacks: 50,
};

const DURATION_OPTIONS = [
  { key: "5_days", label: "5 Days", multiplier: 5 },
  { key: "1_month", label: "1 Month", multiplier: 30 },
];

export const Subscription: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [duration, setDuration] = useState<SubscriptionDuration>("5_days");
  const [frequency, setFrequency] = useState<SubscriptionFrequency>("weekday");
  const [selectedMeals, setSelectedMeals] = useState<MealType[]>(["dinner"]);
  const [estimatedCost, setEstimatedCost] = useState(0);

  useEffect(() => {
    const selectedDuration = DURATION_OPTIONS.find((d) => d.key === duration);
    const totalDays = selectedDuration ? selectedDuration.multiplier : 0;

    const dailyCost = selectedMeals.reduce(
      (sum, meal) => sum + (MEAL_PRICES[meal] || 0),
      0
    );

    let effectiveDays = totalDays;
    if (frequency === "weekday") {
      if (totalDays >= 30) effectiveDays = Math.floor(totalDays * (22 / 30));
      else effectiveDays = Math.floor(totalDays * (5 / 7));
    } else if (frequency === "weekend") {
      if (totalDays >= 30) effectiveDays = Math.ceil(totalDays * (8 / 30));
      else effectiveDays = Math.ceil(totalDays * (2 / 7));
    }

    effectiveDays = Math.max(1, effectiveDays);
    setEstimatedCost(dailyCost * effectiveDays);
  }, [duration, frequency, selectedMeals]);

  const toggleMeal = (meal: MealType) => {
    setSelectedMeals((prev) =>
      prev.includes(meal) ? prev.filter((m) => m !== meal) : [...prev, meal]
    );
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Please log in to subscribe");
      navigate("/login");
      return;
    }
    if (selectedMeals.length === 0) {
      toast.error("Please select at least one meal type.");
      return;
    }

    setLoading(true);
    try {
      const config: SubscriptionConfig = {
        duration,
        frequency,
        meals: selectedMeals,
      };

      // For now, just pass config forward to a detailed planner page
      toast.success("Config saved! Proceeding to detailed planner.");
      navigate("/subscription-regular", {
        state: { config, estimatedCost },
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to proceed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Build Your Meal Plan
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* CONTROLS */}
          <div className="lg:col-span-2 space-y-6">
            {/* 1. Duration */}
            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
              <h3 className="font-bold text-gray-800 mb-3 text-lg">1. Duration</h3>
              <div className="flex flex-wrap gap-2">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() =>
                      setDuration(opt.key as SubscriptionDuration)
                    }
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      duration === opt.key
                        ? "bg-orange-600 text-white border-orange-600 shadow-md"
                        : "bg-white text-gray-600 hover:bg-orange-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Frequency */}
            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
              <h3 className="font-bold text-gray-800 mb-3 text-lg">2. Days</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: "all", label: "Every Day", desc: "Mon-Sun" },
                  { key: "weekday", label: "Weekdays Only", desc: "Mon-Fri" },
                  { key: "weekend", label: "Weekends Only", desc: "Sat-Sun" },
                ].map((freq) => (
                  <button
                    key={freq.key}
                    onClick={() =>
                      setFrequency(freq.key as SubscriptionFrequency)
                    }
                    className={`py-3 px-2 rounded-lg border text-sm font-medium transition-all flex flex-col items-center justify-center ${
                      frequency === freq.key
                        ? "bg-blue-600 text-white border-blue-600 shadow-md"
                        : "bg-white text-gray-600 hover:bg-blue-50"
                    }`}
                  >
                    <span className="font-bold">{freq.label}</span>
                    <span
                      className={`text-xs mt-1 ${
                        frequency === freq.key
                          ? "text-blue-100"
                          : "text-gray-400"
                      }`}
                    >
                      {freq.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Meals */}
            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
              <h3 className="font-bold text-gray-800 mb-3 text-lg">
                3. Select Meals
              </h3>
              <div className="space-y-3">
                {[
                  {
                    id: "breakfast",
                    label: "Breakfast",
                    price: MEAL_PRICES.breakfast,
                    sub: "Delivered by 8:00 AM",
                  },
                  {
                    id: "lunch",
                    label: "Lunch",
                    price: MEAL_PRICES.lunch,
                    sub: "Delivered in Morning",
                  },
                  {
                    id: "dinner",
                    label: "Dinner",
                    price: MEAL_PRICES.dinner,
                    sub: "Delivered by 7:30 PM",
                  },
                ].map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedMeals.includes(m.id as MealType)
                        ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-6 h-6 rounded border flex items-center justify-center ${
                          selectedMeals.includes(m.id as MealType)
                            ? "bg-green-500 border-green-500"
                            : "bg-white border-gray-400"
                        }`}
                      >
                        {selectedMeals.includes(m.id as MealType) && (
                          <span className="text-white text-sm">✓</span>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={selectedMeals.includes(m.id as MealType)}
                        onChange={() => toggleMeal(m.id as MealType)}
                      />
                      <div>
                        <span className="block font-bold text-gray-800 text-lg">
                          {m.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {m.sub}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="block font-bold text-gray-900 text-lg">
                        ₹{m.price}
                      </span>
                      <span className="text-xs text-gray-500">/ meal</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* SUMMARY SIDEBAR */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="p-6 bg-gray-900 text-white rounded-xl shadow-xl">
                <h2 className="text-xl font-bold mb-6 text-orange-400">
                  Plan Summary
                </h2>

                <div className="space-y-4 text-sm mb-8 border-b border-gray-700 pb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Duration</span>
                    <span className="font-medium bg-gray-800 px-2 py-1 rounded text-xs">
                      {DURATION_OPTIONS.find((d) => d.key === duration)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Frequency</span>
                    <span className="font-medium capitalize bg-gray-800 px-2 py-1 rounded">
                      {frequency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Meals per day</span>
                    <span className="font-medium bg-gray-800 px-2 py-1 rounded">
                      {selectedMeals.length}
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    Total Estimated Cost
                  </span>
                  <div className="text-4xl font-bold text-white mt-2">
                    ₹{estimatedCost.toLocaleString()}
                  </div>
                </div>

                <Button
                  variant="primary"
                  onClick={handleSubscribe}
                  isLoading={loading}
                  className="w-full py-4 text-lg font-bold shadow-lg shadow-orange-900/20 hover:scale-105 transition-transform"
                  disabled={selectedMeals.length === 0}
                >
                  Continue to Detailed Planner
                </Button>

                <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
                  You can fine-tune dates and meals in the next step before payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
